'use client';

import { environment } from '@/config/environment';
import { isAdblockerIframeAccessError } from '@/lib/sentry-filter';
import { useEffect } from 'react';

export default function SentryLazyInit() {
  useEffect(() => {
    // Only initialize Sentry if enabled in environment config
    if (!environment.sentry?.enabled || !environment.sentry?.dsn) {
      console.log(
        '[Sentry] Sentry is not enabled or DSN is missing, skipping initialization'
      );
      return;
    }

    // Use requestIdleCallback if available, otherwise use setTimeout
    const loadSentry = async () => {
      console.log('Initializing Sentry...');
      const { init } = await import('@sentry/browser');
      console.log('Initializing Sentry DONE...');
      try {
        init({
          dsn: environment.sentry.dsn,

          // Performance monitoring
          tracesSampleRate: environment.sentry.tracesSampleRate,

          // Error filtering - only capture real errors
          beforeSend(event, hint) {
            // Don't send events in development
            if (process.env.NODE_ENV !== 'production') {
              return null;
            }

            if (isAdblockerIframeAccessError(event)) return null;

            // Filter out common non-critical errors
            if (event.exception) {
              const error = hint.originalException;
              if (error && typeof error === 'object' && 'message' in error) {
                const message = String(error.message).toLowerCase();

                // Filter out network errors, script loading errors, etc.
                if (
                  message.includes('network error') ||
                  message.includes('loading chunk') ||
                  message.includes('script error') ||
                  message.includes('non-error promise rejection')
                ) {
                  return null;
                }
              }
            }

            return event;
          },

          // Environment and release info
          environment: environment.sentry.environment,
          release: environment.sentry.release,

          // Privacy settings
          sendDefaultPii: false,

          // Reduce noise from browser extensions
          allowUrls: [
            // Only report errors from your domain
            /https:\/\/omceanbooking\.com/,
            /https:\/\/.*\.omceanbooking\.com/,
          ],

          // Don't capture console.log as breadcrumbs
          integrations: [],

          // Minimal data collection
          maxBreadcrumbs: 10,

          // Don't track user interactions automatically
          replaysSessionSampleRate: 0,
          replaysOnErrorSampleRate: 0,
        });

        console.log('Sentry initialized successfully');
      } catch (error) {
        console.warn('Failed to initialize Sentry:', error);
      }
    };

    // Delay Sentry init until browser is idle (or 3 seconds max)
    const initWhenIdle = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => loadSentry(), { timeout: 3000 });
      } else {
        setTimeout(loadSentry, 3000);
      }
    };

    // Wait for page load first
    if (document.readyState === 'complete') {
      initWhenIdle();
    } else {
      window.addEventListener('load', initWhenIdle, { once: true });
    }
  }, []);

  // This component doesn't render anything
  return null;
}
